import {FACT_TYPE_ORDER, factTypeLabelKey} from '../../memory/factConstants';
import {FIELD_VALUE_LABELS} from '../../activity_log/detail_renderers/labels';

/**
 * El contenido de la hoja "Como lo ve tu IA", derivado del MODELO.
 *
 * `contextSections()` es la ÚNICA definición de qué secciones tiene la hoja y en qué orden. La
 * consumen las dos salidas: el render en pantalla (`IaContextSheet`) y el texto plano del botón
 * Copiar (`buildContextPlainText`). Que sean la misma función es lo que garantiza que lo copiado
 * sea lo mismo que se ve.
 *
 * El mockup hacía las dos cosas al revés: armaba la hoja scrapeando el HTML del archivo y copiaba
 * con `.innerText.replace('Copiar','')` — o sea que un hecho que contuviera la palabra "Copiar"
 * salía mutilado, y cualquier filtro activo en el archivo cambiaba en silencio "lo que recibe tu
 * IA". Acá el dato es el que devuelve `get_client_context`, que es el MISMO endpoint que consume
 * el MCP público: no es una reconstrucción de lo que creemos que ve el asistente.
 */

/** ¿La respuesta de `getClientContext` es un contexto de verdad y no un error de APIClient? */
export function isContextPayload(payload) {
    if (!payload || typeof payload !== 'object') return false;
    if (payload.status !== undefined && payload.status !== 'OK') return false;
    return payload.client_id !== undefined && payload.client_id !== null;
}

function pushLine(lines, value) {
    if (value === null || value === undefined) return;
    const text = String(value).trim();
    if (text) lines.push(text);
}

function profileSection(context, t) {
    const profile = context.profile;
    if (!profile) return null;
    const lines = [];
    const field = (labelKey, value) => {
        if (value === null || value === undefined || String(value).trim() === '') return;
        pushLine(lines, `${t(labelKey)}: ${value}`);
    };
    // El modelo de conversión viaja como enum (`OTHER`, `LEAD_GEN`…): se muestra con su nombre, igual
    // que en la Bitácora y en Configuración (decisión de fede, 26/09 — el rótulo ya está traducido,
    // así que la línea nunca fue literal). Un valor desconocido pasa tal cual.
    const businessTypeKey = FIELD_VALUE_LABELS.business_type[profile.business_type];
    field('ia_booster.iapaper.profile_business_type',
        businessTypeKey ? t(businessTypeKey) : profile.business_type);
    field('ia_booster.iapaper.profile_industry', profile.industry);
    field('ia_booster.iapaper.profile_primary_kpi', profile.primary_kpi);
    pushLine(lines, profile.notes);
    if (!lines.length) return null;
    return {key: 'profile', title: t('ia_booster.iapaper.section_profile'), lines};
}

/**
 * Los flags derivados que el backend manda junto al contexto (`flags`), en orden fijo y CON su
 * valor: un flag en falso es información ("este cliente no tiene ecommerce"), no un flag ausente.
 * Uno que el backend agregue mañana no se pierde — va al final con su clave cruda de título.
 */
const CONTEXT_FLAGS = ['has_paid_media', 'has_analytics', 'has_ecommerce_data'];

/**
 * Qué fuentes ve el asistente de este cliente: los canales conectados y los flags que el backend
 * deriva de ellos. Estaban en el payload de `get_client_context` y la hoja no los dibujaba, con el
 * lede prometiendo que esto es "palabra por palabra" lo que entra al contexto.
 *
 * Los nombres lindos salen de `channel_details`, que viaja en el MISMO payload; sin él se muestra
 * la constante cruda, que es literalmente lo que recibe la IA.
 */
function connectionsSection(context, t) {
    const channels = context.connected_channels || [];
    const flags = context.flags || {};
    const nameByChannel = {};
    (context.channel_details || []).forEach((detail) => {
        if (detail && detail.channel && detail.channel_name) {
            nameByChannel[detail.channel] = detail.channel_name;
        }
    });
    const lines = [];
    channels.forEach(channel => pushLine(lines, nameByChannel[channel] || channel));

    const extra = Object.keys(flags).filter(flag => !CONTEXT_FLAGS.includes(flag));
    CONTEXT_FLAGS.concat(extra).forEach((flag) => {
        if (flags[flag] === undefined) return;
        const label = t(`ia_booster.iapaper.flag_${flag}`, flag);
        const value = t(flags[flag] ? 'activity_log.value_true' : 'activity_log.value_false');
        pushLine(lines, `${label}: ${value}`);
    });

    if (!lines.length) return null;
    return {key: 'connections', title: t('ia_booster.iapaper.section_connections'), lines};
}

function goalsSection(context, t) {
    const goals = context.goals || [];
    if (!goals.length) return null;
    const lines = [];
    goals.forEach((goal) => {
        if (!goal) return;
        pushLine(lines, `${goal.metric}: ${goal.value}`);
    });
    if (!lines.length) return null;
    return {key: 'goals', title: t('ia_booster.iapaper.section_goals'), lines};
}

/**
 * Los hechos, agrupados por tipo y en el orden del retriever (riesgo y restricciones primero):
 * el mismo `FACT_TYPE_ORDER` con el que se ordena el archivo. Un tipo que el backend mande y el
 * front no conozca no se pierde — va al final, con su clave cruda como título.
 */
function factSections(context, t) {
    const facts = context.facts || [];
    const byType = {};
    facts.forEach((fact) => {
        if (!fact) return;
        const type = fact.fact_type || 'OTHER';
        (byType[type] = byType[type] || []).push(fact);
    });
    const extra = Object.keys(byType).filter(type => !FACT_TYPE_ORDER.includes(type));
    const sections = [];
    FACT_TYPE_ORDER.concat(extra).forEach((type) => {
        const items = byType[type];
        if (!items || !items.length) return;
        const lines = [];
        items.forEach(fact => pushLine(lines, fact.statement));
        if (!lines.length) return;
        sections.push({key: `facts_${type}`, title: t(factTypeLabelKey(type)), lines});
    });

    // El aviso de truncamiento no es un detalle: la promesa de la hoja es "esto es, palabra por
    // palabra, lo que recibe tu IA". Si el bundle se cortó en el cap y no lo decimos, la hoja
    // afirma algo falso justamente sobre lo que vino a demostrar.
    if (context.facts_truncated) {
        const shown = facts.length;
        const total = context.facts_total || 0;
        const hidden = Math.max(0, total - shown);
        const note = t('ia_booster.iapaper.facts_truncated', {
            n: hidden, total, cap: context.facts_cap || shown,
            interpolation: {escapeValue: false},
        });
        if (sections.length) sections[sections.length - 1].note = note;
        else sections.push({key: 'facts_truncated', title: null, lines: [], note});
    }
    return sections;
}

function businessUnitsSection(context, t) {
    const units = context.business_units || [];
    if (!units.length) return null;
    const lines = [];
    units.forEach((unit) => {
        if (!unit) return;
        const parts = [unit.name];
        if (unit.channels && unit.channels.length) parts.push(unit.channels.join(', '));
        if (unit.currency) parts.push(unit.currency);
        pushLine(lines, parts.filter(Boolean).join(' · '));
    });
    if (!lines.length) return null;
    return {key: 'business_units', title: t('ia_booster.iapaper.section_business_units'), lines};
}

function memoriesSection(context, t) {
    const memories = context.memories || [];
    if (!memories.length) return null;
    const lines = [];
    memories.forEach(memory => pushLine(lines, memory && memory.text));
    if (!lines.length) return null;
    return {key: 'memories', title: t('ia_booster.iapaper.section_memories'), lines};
}

function eventsSection(context, t) {
    const events = context.recent_events || [];
    if (!events.length) return null;
    const lines = [];
    events.forEach(event => pushLine(lines, event && event.description));
    if (!lines.length) return null;
    return {key: 'recent_events', title: t('ia_booster.iapaper.section_events'), lines};
}

/**
 * Los cambios que el equipo hizo DIRECTO en las plataformas (Meta/Google). Es un bloque SEPARADO
 * del contexto (`recent_platform_changes`, gateado por su propio módulo), no parte de
 * `recent_events`: el retriever lo separa justamente para que estos no desplacen a las reuniones y
 * los objetivos. La hoja lo omitía, y con el lede prometiendo completitud eso es una hoja que miente.
 *
 * El backend los sirve COLAPSADOS (mismo objeto y campo dentro de 24h ⇒ una fila con
 * `occurrences`), así que el `×N` se dice: sin él, "Presupuesto diario 100 → 150" se lee como un
 * cambio cuando fueron nueve. Se escribe literal, igual que en la Bitácora (`ActivityLogEntry`).
 *
 * La nota de `meta_activity_coverage` va acá y no en una sección propia: es una advertencia SOBRE
 * este bloque ("Meta no expone el historial de N cuentas: no asumir que no hubo cambios"), la
 * escribe el backend para el LLM y viaja en el mismo contexto. Por eso la sección se emite aunque
 * no haya ni un cambio — el caso en que la nota más importa es justamente ese.
 */
function platformChangesSection(context, t) {
    const changes = context.recent_platform_changes || [];
    const coverage = context.meta_activity_coverage;
    const note = (coverage && coverage.note) || null;
    const lines = [];
    changes.forEach((change) => {
        if (!change || !change.description) return;
        const times = Number(change.occurrences);
        pushLine(lines, Number.isFinite(times) && times > 1
            ? `${change.description} ×${times}`
            : change.description);
    });
    if (!lines.length && !note) return null;
    const section = {
        key: 'recent_platform_changes',
        title: t('ia_booster.iapaper.section_platform_changes'),
        lines,
    };
    if (note) section.note = note;
    return section;
}

/** Sólo los títulos: el crudo no entra al contexto, el agente lo lee on-demand. */
function documentsSection(context, t) {
    const documents = context.documents || [];
    const lines = [];
    documents.forEach(doc => pushLine(lines, doc && doc.title));
    if (!lines.length && !context.documents_truncated) return null;
    const section = {key: 'documents', title: t('ia_booster.iapaper.section_documents'), lines};
    if (context.documents_truncated) {
        const hidden = Math.max(0, (context.documents_total || 0) - documents.length);
        section.note = t('ia_booster.iapaper.documents_truncated', {
            n: hidden, total: context.documents_total || 0,
            interpolation: {escapeValue: false},
        });
    }
    return section;
}

/**
 * Las secciones de la hoja, en orden fijo:
 * Perfil → Conexiones → Objetivos → Hechos (por tipo) → Líneas de control → Notas →
 * Últimos eventos → Cambios en plataformas → Material.
 *
 * Cada sección es `{key, title, lines, note?}`. Una sección sin contenido no se emite (una hoja
 * llena de títulos vacíos no dice "no hay nada", dice "esto está roto").
 *
 * **El orden es de LECTURA, pero la cobertura tiene que ser la del payload.** Cada bloque que
 * `get_client_context` devuelve tiene su sección acá; si el backend suma uno, hay que sumarlo —
 * el lede promete que esto es palabra por palabra lo que entra al contexto, así que un bloque sin
 * dibujar convierte la promesa en una mentira. Hay test guardián sobre las claves del payload.
 */
export function contextSections(context, t) {
    if (!isContextPayload(context)) return [];
    const sections = [];
    const push = (section) => { if (section) sections.push(section); };
    push(profileSection(context, t));
    push(connectionsSection(context, t));
    push(goalsSection(context, t));
    factSections(context, t).forEach(push);
    push(businessUnitsSection(context, t));
    push(memoriesSection(context, t));
    push(eventsSection(context, t));
    push(platformChangesSection(context, t));
    push(documentsSection(context, t));
    return sections;
}

/** El encabezado de la hoja: qué es, de qué cliente y con qué id lo pide el asistente. */
export function contextEyebrow(context, t) {
    if (!isContextPayload(context)) return '';
    return t('ia_booster.iapaper.eyebrow', {
        client: context.client_name || '',
        id: context.client_id,
        interpolation: {escapeValue: false},
    });
}

/**
 * El texto plano del botón Copiar. Se construye del MODELO y recorre exactamente las mismas
 * secciones que se ven — el guion de cada línea incluido, que en pantalla lo pone el CSS.
 */
export function buildContextPlainText(context, t) {
    if (!isContextPayload(context)) return '';
    const out = [contextEyebrow(context, t), t('ia_booster.iapaper.title'), ''];
    contextSections(context, t).forEach((section) => {
        if (section.title) out.push(section.title.toUpperCase());
        section.lines.forEach(line => out.push(`— ${line}`));
        if (section.note) out.push(section.note);
        out.push('');
    });
    return out.join('\n').trim();
}

// El copiado al portapapeles se mudó a `utils/copyPlainText`: lo usan también los chips del portal,
// y tenerlo acá ataba una pantalla del portal al Recorrido sin ninguna razón de dominio.
